import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { mailboxService } from '../services/mailboxService';
import { useAuth } from './AuthContext';

const AccountContext = createContext();

export function AccountProvider({ children }) {
  const { user } = useAuth();
  const [mailboxes, setMailboxes] = useState([]);
  const [activeMailboxIds, setActiveMailboxIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  // Map: mailboxId -> { folderType -> folderId }
  const [folderMaps, setFolderMaps] = useState({});

  useEffect(() => {
    if (user) {
      loadMailboxes();
    } else {
      setMailboxes([]);
      setActiveMailboxIds(new Set());
      setFolderMaps({});
      setLoading(false);
    }
  }, [user]);

  const loadMailboxes = async () => {
    try {
      const data = await mailboxService.getMailboxes();
      const items = Array.isArray(data) ? data : (data?.mailboxes || []);
      setMailboxes(items);
      const ids = new Set(items.map(m => m.mailboxId || m.mailboxID || m.id));
      setActiveMailboxIds(ids);

      // Load folders for each mailbox to build type->id maps
      const maps = {};
      await Promise.all(items.map(async (mb) => {
        const mbId = mb.mailboxId || mb.mailboxID || mb.id;
        try {
          const folders = await mailboxService.getFolders(mbId);
          const typeMap = {};
          const idMap = {};
          for (const f of folders) {
            typeMap[f.folderType] = f.folderId || f.folderID;
            idMap[f.folderId || f.folderID] = f.folderType;
          }
          maps[mbId] = { byType: typeMap, byId: idMap };
        } catch {
          maps[mbId] = { byType: {}, byId: {} };
        }
      }));
      setFolderMaps(maps);
    } catch (err) {
      console.error('[Webmail] Failed to load mailboxes for account context:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleMailbox = useCallback((mailboxId) => {
    setActiveMailboxIds((prev) => {
      const next = new Set(prev);
      if (next.has(mailboxId)) {
        if (next.size > 1) {
          next.delete(mailboxId);
        }
      } else {
        next.add(mailboxId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setActiveMailboxIds(new Set(mailboxes.map(m => m.mailboxId || m.mailboxID || m.id)));
  }, [mailboxes]);

  // Given a folderId (from the URL), resolve the equivalent folder for a
  // different mailbox by matching folder TYPE. Returns null if no match.
  const resolveFolderForMailbox = useCallback((sourceFolderId, targetMailboxId) => {
    if (!sourceFolderId || !folderMaps) return null;
    // Find which mailbox this folder belongs to and what type it is
    let folderType = null;
    for (const [, map] of Object.entries(folderMaps)) {
      if (map.byId[sourceFolderId] != null) {
        folderType = map.byId[sourceFolderId];
        break;
      }
    }
    if (folderType == null) return null;
    // Look up the same type in the target mailbox
    const targetMap = folderMaps[targetMailboxId];
    if (!targetMap) return null;
    return targetMap.byType[folderType] || null;
  }, [folderMaps]);

  // Get folder type for a given folderId
  const getFolderType = useCallback((folderId) => {
    if (!folderId || !folderMaps) return null;
    for (const [, map] of Object.entries(folderMaps)) {
      if (map.byId[folderId] != null) {
        return map.byId[folderId];
      }
    }
    return null;
  }, [folderMaps]);

  const allEmails = mailboxes.map(m => (m.emailAddress || m.email || '').toLowerCase());

  const activeMailboxes = mailboxes.filter(
    m => activeMailboxIds.has(m.mailboxId || m.mailboxID || m.id)
  );

  const isMultiAccount = mailboxes.length > 1;

  return (
    <AccountContext.Provider value={{
      mailboxes,
      activeMailboxIds,
      activeMailboxes,
      allEmails,
      isMultiAccount,
      loading,
      folderMaps,
      toggleMailbox,
      selectAll,
      resolveFolderForMailbox,
      getFolderType,
      refreshMailboxes: loadMailboxes,
    }}>
      {children}
    </AccountContext.Provider>
  );
}

export function useAccounts() {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccounts must be used within an AccountProvider');
  }
  return context;
}
